import { AnimatePresence, motion } from 'motion/react';
import { FormEventHandler, ReactNode, useId } from 'react';
import styled, { css } from 'styled-components';

import { Button } from '@creditornot/alchemy-react';
import { borderWidth, color, space } from '@creditornot/alchemy-react/tokens';
import { ApiErrorBanner } from 'common/components/alchemy/ApiErrorBanner/ApiErrorBanner';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useMatchMedia } from 'common/hooks/useMatchMedia';
import { media } from 'common/media';

import { useFormScroll } from './useFormScroll';

const Footer = styled.div<{ $wideStepNav: boolean }>`
  display: flex;
  padding: ${space.regularXxxlarge};
  padding-top: ${space.regularXxlarge};

  & > * {
    flex: 1 1 auto;
  }

  @media ${media.md} {
    padding: ${space.broadLarge};
    padding-top: ${space.regularXxlarge};

    ${({ $wideStepNav }) =>
      !$wideStepNav &&
      css`
        justify-content: flex-end;

        & > * {
          flex: initial;
          min-width: 100px;
        }
      `}
  }
`;

/** Wraps back + next/submit on stepped desktop. */
const FooterCompactGroup = styled.div`
  align-items: center;
  display: flex;
  gap: ${space.regularMedium};
  justify-content: flex-end;
  min-width: 0;
`;

const StyledForm = styled.form<{ $layout: 'responsive' | 'stepped' }>`
  counter-reset: step;
  display: flex;
  flex: 1 1 auto;
  flex-direction: row;
  overflow: hidden;
  scroll-behavior: smooth;
  scroll-snap-type: x mandatory;

  ${({ $layout }) =>
    $layout === 'stepped'
      ? css`
          box-sizing: border-box;
          overflow-x: auto;
          scrollbar-width: none;

          &::-webkit-scrollbar {
            display: none;
          }

          padding: 0 ${space.regularMedium};

          @media ${media.md} {
            padding: 0 ${space.regularMedium};
          }

          & > * {
            border: 0;
            border-radius: 0;
            box-sizing: border-box;
            flex: 0 0 100%;
            margin: 0;
            width: 100%;
            scroll-snap-align: center;
          }
        `
      : css`
          overflow: hidden;

          & > * {
            border-width: ${borderWidth.regularMedium};
            border-style: solid;
            border-color: ${color.border};
            border-radius: 32px;
            flex: 1 0 calc(100vw - ${space.regularXxxlarge} * 2);
            margin: 0 ${space.regularXxxlarge};
            scroll-snap-align: center;
          }

          @media ${media.md} {
            border-width: ${borderWidth.regularMedium};
            border-style: solid;
            border-color: ${color.border};
            border-radius: 32px;
            margin: 0 ${space.broadLarge};

            & > * {
              border: 0;
              border-radius: 0;
              flex: 1 0 0;
              margin: 0;
            }

            & > * ~ * {
              border-left-width: ${borderWidth.regularMedium};
              border-left-style: solid;
              border-left-color: ${color.border};
            }
          }
        `}
`;

const StyledApiErrorBanner = styled(ApiErrorBanner)`
  margin: ${space.regularXxlarge} ${space.regularXxxlarge} 0 ${space.regularXxxlarge};

  @media ${media.md} {
    margin: ${space.regularXxlarge} ${space.broadLarge} 0 ${space.broadLarge};
  }
`;

type Props = {
  children: ReactNode;
  error: Error | null;
  isSubmitting?: boolean;
  /**
   * `responsive` — one step + next/back on small screens; side‑by‑side steps on desktop (default).
   * `stepped` — always a single visible step with next/back. On small screens the footer matches the
   * wide mobile step controls; on desktop, back/next/submit use the usual compact, right-aligned footer.
   */
  layout?: 'responsive' | 'stepped';
  /** When true, the primary submit control is not shown (e.g. read-only preview step). */
  hideSubmitButton?: boolean;
  noValidate?: boolean;
  onSubmit: FormEventHandler<HTMLFormElement>;
  submitButtonLabel: string;
};

export function Form({
  children,
  error,
  isSubmitting,
  layout = 'responsive',
  hideSubmitButton = false,
  noValidate = false,
  onSubmit,
  submitButtonLabel,
}: Props) {
  const isMobile = useMatchMedia('maxMd');
  const f = useFormatMessage();
  const formId = useId();
  const useStepNav = isMobile || layout === 'stepped';
  const wideStepFooter = isMobile && useStepNav;
  const { ref, jump, isLastStep, isFirstStep } = useFormScroll(useStepNav);

  const isBackButtonVisible = useStepNav && !isFirstStep;
  const isNextButtonVisible = useStepNav && !isLastStep;
  /** Default desktop footer: only submit — `AnimatePresence` + `motion` as direct children of `Footer`. */
  const defaultDesktopFooter = !wideStepFooter && !isBackButtonVisible && !isNextButtonVisible;

  return (
    <>
      <StyledForm
        $layout={layout}
        id={formId}
        onSubmit={onSubmit}
        ref={ref}
        noValidate={noValidate}
      >
        {children}
      </StyledForm>
      <StyledApiErrorBanner error={error} />
      <Footer $wideStepNav={wideStepFooter}>
        {wideStepFooter ? (
          <>
            <AnimatePresence>
              {isBackButtonVisible && (
                <motion.div
                  animate={{ opacity: 1, paddingRight: space.narrowSmall, width: '50%' }}
                  exit={{ opacity: 0, paddingRight: 0, width: '0%' }}
                  initial={{ opacity: 0, paddingRight: 0, width: '0%' }}
                  key="back-button"
                >
                  <Button
                    onClick={() => jump(-1)}
                    size="medium"
                    style={{ width: '100%' }}
                    variant="secondaryNeutral"
                  >
                    {f('common.back')}
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
            <motion.div
              animate={{
                paddingLeft: isBackButtonVisible ? space.narrowSmall : 0,
                width: isBackButtonVisible ? '50%' : '100%',
              }}
              initial={false}
            >
              {isNextButtonVisible ? (
                <Button
                  onClick={() => jump(1)}
                  size="medium"
                  style={{ width: '100%' }}
                  variant="primary"
                >
                  {f('common.next')}
                </Button>
              ) : hideSubmitButton ? null : (
                <Button
                  disabled={isSubmitting}
                  form={formId}
                  loading={isSubmitting}
                  size="medium"
                  style={{ width: '100%' }}
                  type="submit"
                  variant="primary"
                >
                  {submitButtonLabel}
                </Button>
              )}
            </motion.div>
          </>
        ) : defaultDesktopFooter ? (
          <>
            <AnimatePresence>
              {isBackButtonVisible && (
                <motion.div
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 'auto' }}
                  initial={{ opacity: 0, width: 'auto' }}
                  key="back-button"
                >
                  <Button onClick={() => jump(-1)} size="medium" variant="secondaryNeutral">
                    {f('common.back')}
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
            <motion.div
              animate={
                useStepNav
                  ? {
                      paddingLeft: isBackButtonVisible ? space.narrowSmall : 0,
                      width: isBackButtonVisible ? '50%' : '100%',
                    }
                  : undefined
              }
              initial={false}
            >
              {isNextButtonVisible ? (
                <Button
                  onClick={() => jump(1)}
                  size="medium"
                  style={{ width: '100%' }}
                  variant="primary"
                >
                  {f('common.next')}
                </Button>
              ) : hideSubmitButton ? null : (
                <Button
                  disabled={isSubmitting}
                  form={formId}
                  loading={isSubmitting}
                  size="medium"
                  style={{ width: '100%' }}
                  type="submit"
                  variant="primary"
                >
                  {submitButtonLabel}
                </Button>
              )}
            </motion.div>
          </>
        ) : (
          <FooterCompactGroup>
            <AnimatePresence>
              {isBackButtonVisible && (
                <motion.div
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 'auto' }}
                  initial={{ opacity: 0, width: 'auto' }}
                  key="back-button"
                >
                  <Button onClick={() => jump(-1)} size="medium" variant="secondaryNeutral">
                    {f('common.back')}
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
            <motion.div animate={{ width: 'auto' }} initial={false}>
              {isNextButtonVisible ? (
                <Button onClick={() => jump(1)} size="medium" variant="primary">
                  {f('common.next')}
                </Button>
              ) : hideSubmitButton ? null : (
                <Button
                  disabled={isSubmitting}
                  form={formId}
                  loading={isSubmitting}
                  size="medium"
                  type="submit"
                  variant="primary"
                >
                  {submitButtonLabel}
                </Button>
              )}
            </motion.div>
          </FooterCompactGroup>
        )}
      </Footer>
    </>
  );
}
